<template>
  <div class="login-container" :style="backgroundStyle">
    <v-card class="login-form mx-auto" elevation="8" rounded="lg" max-width="400">
      <v-card-title class="text-center py-6" style="margin-top: -2rem;">
        <div style="font-size: 2rem; font-weight: 600; font-family: 'Orbitron', sans-serif; letter-spacing: 2px;">MODATO</div>
        <div style="font-size: 1.2rem; font-weight: normal; letter-spacing: 1px;">Admin Center</div>
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="login">
          <v-text-field
            v-model="email"
            label="Email"
            prepend-inner-icon="mdi-email-outline"
            variant="outlined"
            density="compact"
            placeholder="Email address"
            required
            class="mb-4"
            :error-messages="emailError"
          ></v-text-field>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            label="Password"
            prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            variant="outlined"
            density="compact"
            placeholder="Enter your password"
            required
            @click:append-inner="showPassword = !showPassword"
            :error-messages="passwordError"
          ></v-text-field>
          <v-checkbox
            v-model="rememberMe"
            label="Remember Me"
            color="primary"
            hide-details
            class="mt--3"
          ></v-checkbox>
          <v-btn
            type="submit"
            color="primary"
            size="large"
            variant="tonal"
            block
            class="mt-3"
            :loading="loading"
          >
            Login
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import secureAxios from '../utils/secureAxios';
import { jwtDecode } from 'jwt-decode';

// Fallback JWT decoder in case jwt-decode fails
function jwtDecodeFallback(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(payload);
  } catch (e) {
    console.error('jwtDecode fallback failed:', e);
    return null;
  }
}

export default {
  name: 'LoginForm',
  data() {
    return {
      email: localStorage.getItem('rememberedEmail') || '',
      password: '',
      showPassword: false,
      rememberMe: !!localStorage.getItem('rememberedEmail'),
      loading: false,
      emailError: '',
      passwordError: '',
    };
  },
  computed: {
    backgroundStyle() {
      return {
        'background-image': `url(${require('@/assets/LoginBackground.png')})`,
        'background-size': 'cover',
        'background-position': 'center',
        'background-repeat': 'no-repeat',
      };
    },
  },
  methods: {
    async login() {
      this.emailError = '';
      this.passwordError = '';

      if (!this.email) {
        this.emailError = 'Email is required';
        return;
      }
      if (!this.password) {
        this.passwordError = 'Password is required';
        return;
      }

      this.loading = true;
      try {
        const clientTime = Math.floor(Date.now() / 1000);
        console.log('Sending login request to:', '/api/users/login');
        console.log('Request payload:', { email: this.email, password: this.password, clientTime });
        const response = await secureAxios.post('/api/users/login', {
          email: this.email,
          password: this.password,
          clientTime,
        }, {
          timeout: 10000,
        });

        console.log('Login Response Received:', response.data);
        const receivedToken = response.data.token;
        const receivedUser = response.data.user;

        if (!receivedToken) {
          console.error('🔥 CRITICAL: No token received from server!');
          toast.error('Login failed: No token received.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        if (!receivedUser) {
          console.error('🔥 CRITICAL: No user data received from server!');
          toast.error('Login failed: No user data received.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        // Try using jwt-decode, fall back to custom decoder if it fails
        let decoded;
        try {
          decoded = jwtDecode(receivedToken);
        } catch (decodeError) {
          console.error('jwt-decode failed, using fallback:', decodeError);
          decoded = jwtDecodeFallback(receivedToken);
        }

        if (!decoded) {
          console.error('🔥 CRITICAL: Failed to decode token!');
          toast.error('Login failed: Invalid token.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        console.log('Decoded token after login:', decoded);
        console.log('Current time (seconds):', Math.floor(Date.now() / 1000));
        console.log('Token expires at (seconds):', decoded.exp);
        console.log('Token is valid:', decoded.exp > Math.floor(Date.now() / 1000));

        // Save to localStorage with error handling
        try {
          console.log('Attempting to set authToken:', receivedToken);
          localStorage.setItem('authToken', receivedToken);
          const tokenAfterSet = localStorage.getItem('authToken');
          console.log('Value of authToken immediately after set:', tokenAfterSet);
          if (tokenAfterSet !== receivedToken) {
            console.error('🔥 CRITICAL: authToken failed to set or retrieve correctly in localStorage!');
            toast.error('Failed to save authentication token.', { autoClose: 5000 });
            this.loading = false;
            return;
          }
        } catch (storageError) {
          console.error('🔥 CRITICAL: Failed to set authToken in localStorage:', storageError);
          toast.error('Failed to save authentication token.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        try {
          const userString = JSON.stringify(receivedUser);
          console.log('Attempting to set user:', userString);
          localStorage.setItem('user', userString);
          const userAfterSet = localStorage.getItem('user');
          console.log('Value of user immediately after set:', userAfterSet);
          if (userAfterSet !== userString) {
            console.error('🔥 CRITICAL: user failed to set or retrieve correctly in localStorage!');
            toast.error('Failed to save user data.', { autoClose: 5000 });
            this.loading = false;
            return;
          }
        } catch (storageError) {
          console.error('🔥 CRITICAL: Failed to set user in localStorage:', storageError);
          toast.error('Failed to save user data.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        try {
          if (this.rememberMe) {
            console.log('Setting rememberedEmail:', this.email);
            localStorage.setItem('rememberedEmail', this.email);
          } else {
            console.log('Removing rememberedEmail from localStorage');
            localStorage.removeItem('rememberedEmail');
          }
        } catch (storageError) {
          console.error('🔥 CRITICAL: Failed to set/remove rememberedEmail in localStorage:', storageError);
          toast.error('Failed to save email preference.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        console.log('✅ Login success, redirecting immediately to dashboard...');
        this.$router.push('/dashboard');
        toast.success('Logged in successfully', { autoClose: 3000 });
      } catch (error) {
        console.error('Login error:', error);
        if (error.response) {
          console.log('Error response status:', error.response.status);
          console.log('Error response data:', error.response.data);
          console.log('Error response headers:', error.response.headers);
          if (error.response.status === 401) {
            toast.error('Invalid credentials', { autoClose: 5000 });
          } else if (error.response.status === 400) {
            error.response.data.errors?.forEach(err => {
              if (err.param === 'email') this.emailError = err.msg;
              if (err.param === 'password') this.passwordError = err.msg;
            });
          } else if (error.response.status === 429) {
            toast.error('Too many login attempts, please try again later', { autoClose: 5000 });
          } else {
            toast.error(`Login failed: ${error.response.data.message || 'Unknown error'}`, { autoClose: 5000 });
          }
        } else if (error.request) {
          console.log('No response received:', error.request);
          toast.error('Network error: Unable to reach the server', { autoClose: 5000 });
        } else {
          console.log('Error setting up request:', error.message);
          toast.error(`Network error: ${error.message}`, { autoClose: 5000 });
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

. 
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600&display=swap');
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  margin: 0;
  padding: 0;
}

.login-form {
  background: rgba(20, 20, 20, 0.95);
  padding: 2rem;
  width: 100%;
  color: white;
}

.v-input input,
.v-input__control {
  color: white;
}

.v-label {
  color: rgba(255, 255, 255, 0.7);
}

.v-checkbox .v-label {
  color: rgba(255, 255, 255, 0.9);
}

.v-btn {
  color: white;
}
</style>